import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Heart, ShoppingBag, User, Menu, X, LogOut, LayoutDashboard } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { API, logErr, inr } from "../lib/api";

const LINKS = [
  { label: "Streetwear", to: "/shop?category=streetwear" },
  { label: "Casual", to: "/shop?category=casual" },
  { label: "Customize", to: "/customize" },
  { label: "New Drops", to: "/shop?sort=newest&tag=new" },
  { label: "Track Order", to: "/track" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { count, pop } = useCart();
  const [q, setQ] = useState("");
  const [suggests, setSuggests] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const navigate = useNavigate();
  const sugRef = useRef(null);

  useEffect(() => {
    if (q.trim().length < 2) { setSuggests([]); return; }
    const t = setTimeout(async () => {
      try { setSuggests((await API.get("/products/suggest", { params: { q } })).data); }
      catch (err) { logErr("Search suggestions failed:", err); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const h = (e) => { if (sugRef.current && !sugRef.current.contains(e.target)) setShowSug(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    setShowSug(false);
    setMobileOpen(false);
    navigate(`/shop?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <header className="sticky top-0 z-50 bg-ink/85 backdrop-blur-xl border-b border-[#262626]" data-testid="navbar">
      <div className="bg-gold text-ink text-center text-xs font-bold tracking-[0.25em] uppercase py-1.5">
        Free shipping over ₹1,499 &mdash; Wear your identity
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center gap-4 h-16">
        <button className="lg:hidden text-foreground" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
          <Menu size={22} />
        </button>
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="nav-logo">
          <img src="/logo.png" alt="Dressedin by Urbanz" className="h-10 w-10 object-contain" />
          <span className="font-display font-extrabold tracking-widest text-sm hidden sm:block">
            <span className="gold-text">DRESSEDIN</span>
            <span className="block text-[9px] text-muted-foreground tracking-[0.35em]">BY URBANZ</span>
          </span>
        </Link>
        <nav className="hidden lg:flex items-center gap-6 ml-6">
          {LINKS.map((l) => (
            <Link key={l.label} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(" ", "-")}`}
              className="text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-gold transition-colors">
              {l.label}
            </Link>
          ))}
        </nav>
        <div className="flex-1" />
        <div className="relative hidden md:block w-64" ref={sugRef}>
          <form onSubmit={submitSearch}>
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              data-testid="search-input"
              value={q}
              onChange={(e) => { setQ(e.target.value); setShowSug(true); }}
              onFocus={() => setShowSug(true)}
              placeholder="Search hoodies, blazers..."
              className="w-full bg-ink border border-[#262626] pl-9 pr-3 py-2 text-sm outline-none focus:border-gold transition-colors"
            />
          </form>
          {showSug && suggests.length > 0 && (
            <div className="absolute top-full mt-1 left-0 right-0 bg-ink-surface border border-[#262626] z-50" data-testid="search-suggestions">
              {suggests.map((s) => (
                <Link key={s.id} to={`/product/${s.slug}`} onClick={() => { setShowSug(false); setQ(""); }}
                  data-testid={`suggestion-${s.slug}`}
                  className="flex items-center gap-3 p-2 hover:bg-ink-elevated transition-colors">
                  <img src={s.images?.[0]} alt="" className="h-10 w-10 object-cover" loading="lazy" />
                  <div className="min-w-0">
                    <p className="text-xs font-semibold truncate">{s.name}</p>
                    <p className="text-xs text-gold">{inr(s.sale_price || s.price)}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
        <Link to="/wishlist" className="text-muted-foreground hover:text-gold transition-colors" data-testid="nav-wishlist">
          <Heart size={20} />
        </Link>
        <Link to="/cart" className={`relative text-muted-foreground hover:text-gold transition-colors ${pop ? "animate-cart-pop" : ""}`} data-testid="nav-cart">
          <ShoppingBag size={20} />
          {count > 0 && (
            <span data-testid="cart-count" className="absolute -top-2 -right-2 bg-gold text-ink text-[10px] font-bold h-4 min-w-4 px-0.5 flex items-center justify-center">
              {count}
            </span>
          )}
        </Link>
        <div className="relative">
          <button onClick={() => setUserMenu(!userMenu)} className="text-muted-foreground hover:text-gold transition-colors" data-testid="nav-account">
            <User size={20} />
          </button>
          {userMenu && (
            <div className="absolute right-0 top-full mt-2 w-44 bg-ink-surface border border-[#262626] z-50" data-testid="account-menu">
              {user ? (
                <>
                  <div className="px-4 py-3 border-b border-[#262626]">
                    <p className="text-xs font-bold truncate">{user.name}</p>
                    <p className="text-[10px] text-muted-foreground truncate">{user.email}</p>
                  </div>
                  {user.role === "admin" && (
                    <Link to="/admin" onClick={() => setUserMenu(false)} data-testid="menu-admin"
                      className="flex items-center gap-2 px-4 py-2.5 text-xs text-gold hover:bg-ink-elevated">
                      <LayoutDashboard size={14} /> Admin Panel
                    </Link>
                  )}
                  <Link to="/account" onClick={() => setUserMenu(false)} data-testid="menu-account"
                    className="block px-4 py-2.5 text-xs hover:bg-ink-elevated">My Account</Link>
                  <button onClick={() => { logout(); setUserMenu(false); }} data-testid="menu-logout"
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-xs text-red-400 hover:bg-ink-elevated">
                    <LogOut size={14} /> Logout
                  </button>
                </>
              ) : (
                <Link to="/auth" onClick={() => setUserMenu(false)} data-testid="menu-login"
                  className="block px-4 py-3 text-xs text-gold hover:bg-ink-elevated">Login / Sign Up</Link>
              )}
            </div>
          )}
        </div>
      </div>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 bg-ink flex flex-col" data-testid="mobile-menu">
          <div className="flex items-center justify-between p-4 border-b border-[#262626]">
            <img src="/logo.png" alt="logo" className="h-10 w-10 object-contain" />
            <button onClick={() => setMobileOpen(false)} data-testid="mobile-menu-close"><X size={24} /></button>
          </div>
          <form onSubmit={submitSearch} className="p-4 border-b border-[#262626]">
            <input data-testid="mobile-search-input" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search products..."
              className="w-full bg-ink-surface border border-[#262626] px-4 py-3 text-sm outline-none focus:border-gold" />
          </form>
          <nav className="flex flex-col p-4 gap-1">
            {LINKS.map((l) => (
              <Link key={l.label} to={l.to} onClick={() => setMobileOpen(false)}
                className="font-display text-2xl font-bold py-3 border-b border-[#262626] hover:text-gold transition-colors">
                {l.label}
              </Link>
            ))}
            {!user && (
              <Link to="/auth" onClick={() => setMobileOpen(false)} className="font-display text-2xl font-bold py-3 text-gold">
                Login / Sign Up
              </Link>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
